'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useCart } from '@/context/cart-context';
import { useAuth } from '@/context/auth-context';
import { formatPrice } from '@/lib/shopify';
import type { NavGroup } from '@/lib/shopify/nav';
import type { Product } from '@/lib/shopify/types';


function SearchIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function BasketIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </svg>
  );
}

function ContactIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function findGroupIdForPath(navGroups: NavGroup[], pathname: string) {
  return navGroups.find((group) =>
    group.items.some((item) =>
      item.href === pathname ||
      item.sections?.some((section) => section.items.some((link) => link.href === pathname))
    )
  )?.id;
}

interface SearchCategorySuggestion {
  title: string;
  href: string;
  context: string;
  score: number;
}

function normalizeSearchText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function getCategorySearchSuggestions(navGroups: NavGroup[], query: string): SearchCategorySuggestion[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 2) return [];

  const suggestions: SearchCategorySuggestion[] = [];
  const seen = new Set<string>();

  const addSuggestion = (title: string, href: string, context: string) => {
    if (seen.has(href)) return;

    const normalizedTitle = normalizeSearchText(title);
    const normalizedHref = normalizeSearchText(href.replace('/collections/', ''));
    const matchesTitle = normalizedTitle.includes(normalizedQuery);
    const matchesHref = normalizedHref.includes(normalizedQuery);
    if (!matchesTitle && !matchesHref) return;

    seen.add(href);
    suggestions.push({
      title,
      href,
      context,
      score:
        normalizedTitle === normalizedQuery ? 0 :
        normalizedTitle.startsWith(normalizedQuery) ? 1 :
        matchesTitle ? 2 :
        3,
    });
  };

  for (const group of navGroups) {
    addSuggestion(group.name, group.href, 'Category');

    for (const item of group.items) {
      addSuggestion(item.title, item.href, group.name);

      for (const section of item.sections ?? []) {
        for (const link of section.items) {
          addSuggestion(link.title, link.href, section.title);
        }
      }
    }
  }

  return suggestions
    .sort((a, b) => a.score - b.score || a.title.length - b.title.length || a.title.localeCompare(b.title))
    .slice(0, 5);
}

export default function Header({ navGroups }: { navGroups: NavGroup[] }) {
  const router = useRouter();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenGroups, setMobileOpenGroups] = useState<string[]>([]);
  const [mobileOpenItems, setMobileOpenItems] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState(
    () => findGroupIdForPath(navGroups, pathname) ?? navGroups[0]?.id ?? ''
  );
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [openMegaId, setOpenMegaId] = useState<string | null>(null);
  const megaCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [subnavOffset, setSubnavOffset] = useState(0);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const subnavTrackRef = useRef<HTMLDivElement>(null);
  const subnavInnerRef = useRef<HTMLDivElement>(null);

  const openMega = (id: string) => {
    if (megaCloseTimer.current) {
      clearTimeout(megaCloseTimer.current);
      megaCloseTimer.current = null;
    }
    setOpenMegaId(id);
  };

  const { cart } = useCart();
  const { customer } = useAuth();
  const itemCount = cart?.totalQuantity ?? 0;
  const activeTab = hoveredTab ?? selectedTab;
  const activeGroup = navGroups.find((g) => g.id === activeTab);
  const accountLabel = customer?.firstName || (customer ? 'Account' : 'Account');
  const contextualMenuItem =
    activeGroup?.items.find((item) =>
      item.href === pathname ||
      item.sections?.some((section) => section.items.some((link) => link.href === pathname))
    );
  const showSubnav = (activeGroup?.items.length ?? 0) > 0;
  const categorySearchResults = useMemo(
    () => getCategorySearchSuggestions(navGroups, searchQuery),
    [navGroups, searchQuery]
  );

  useEffect(() => {
    const matchingGroupId = findGroupIdForPath(navGroups, pathname);
    if (matchingGroupId) {
      setSelectedTab(matchingGroupId);
      setHoveredTab(null);
    }
  }, [navGroups, pathname]);

  useEffect(() => {
    if (!showSubnav) {
      setSubnavOffset(0);
      return;
    }

    const updateSubnavOffset = () => {
      const tab = tabRefs.current.get(activeTab);
      const track = subnavTrackRef.current;
      const inner = subnavInnerRef.current;

      if (!tab || !track || !inner) return;

      const tabRect = tab.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();
      const trackStyles = window.getComputedStyle(track);
      const trackPaddingLeft = parseFloat(trackStyles.paddingLeft) || 0;
      const trackPaddingRight = parseFloat(trackStyles.paddingRight) || 0;
      const trackContentLeft = trackRect.left + trackPaddingLeft;
      const trackContentWidth = trackRect.width - trackPaddingLeft - trackPaddingRight;
      const desiredOffset = tabRect.left - trackContentLeft;
      const maxOffset = Math.max(0, trackContentWidth - innerRect.width);
      const nextOffset = Math.round(Math.min(Math.max(desiredOffset, 0), maxOffset));

      setSubnavOffset((currentOffset) =>
        currentOffset === nextOffset ? currentOffset : nextOffset
      );
    };

    const frame = window.requestAnimationFrame(updateSubnavOffset);
    window.addEventListener('resize', updateSubnavOffset);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateSubnavOffset);
    };
  }, [activeTab, activeGroup?.items.length, showSubnav]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/search/predictive?q=${encodeURIComponent(q)}&limit=6`,
          { signal: ctrl.signal }
        );
        const data = await r.json();
        setSearchResults(data.products ?? []);
      } catch {
        // ignore aborts and network blips
      } finally {
        setSearchLoading(false);
      }
    }, 200);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [searchQuery]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!openMegaId) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inNav = document.querySelector('.header-nav-wrapper')?.contains(target);
      if (!inNav) setOpenMegaId(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openMegaId]);

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchOpen(false);
    router.push(`/search?q=${encodeURIComponent(q)}`);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileGroup = (id: string) => {
    setMobileOpenGroups((openGroups) =>
      openGroups.includes(id)
        ? openGroups.filter((groupId) => groupId !== id)
        : [...openGroups, id]
    );
  };

  const toggleMobileItem = (href: string) => {
    setMobileOpenItems((openItems) =>
      openItems.includes(href)
        ? openItems.filter((itemHref) => itemHref !== href)
        : [...openItems, href]
    );
  };

  return (
    <header className="header">
      {/* Main Header — logo | search | icons */}
      <div className="header-main">
        <div className="container header-main-inner">
          {/* Mobile Menu Button */}
          <button
            className="header-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            <span className="header-mobile-toggle-label">Menu</span>
          </button>

          {/* Logo */}
          <Link href="/" className="header-logo">
            Bathroom<span className="header-logo-accent">fort</span>
          </Link>

          {/* Search Bar */}
          <div className="header-search" ref={searchWrapRef}>
            <form
              role="search"
              onSubmit={submitSearch}
              className="header-search-form"
            >
              <label htmlFor="header-search-input" className="sr-only">What are you looking for?</label>
              <input
                id="header-search-input"
                name="q"
                type="search"
                placeholder="What are you looking for?"
                className="header-search-input"
                maxLength={512}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                autoComplete="off"
              />
              <button className="header-search-btn" aria-label="Search" type="submit">
                <SearchIcon />
              </button>
            </form>

            {searchOpen && searchQuery.trim().length >= 2 && (
              <div className="header-search-dropdown" role="listbox">
                {searchLoading && searchResults.length === 0 && categorySearchResults.length === 0 ? (
                  <div className="header-search-status">Searching…</div>
                ) : searchResults.length === 0 && categorySearchResults.length === 0 ? (
                  <div className="header-search-status">
                    No categories or products match “{searchQuery.trim()}”.
                  </div>
                ) : (
                  <>
                    {categorySearchResults.length > 0 && (
                      <div className="header-search-section">
                        <p className="header-search-section-title">Categories</p>
                        <ul className="header-search-category-list">
                          {categorySearchResults.map((category) => (
                            <li key={category.href}>
                              <Link
                                href={category.href}
                                className="header-search-category"
                                onClick={() => setSearchOpen(false)}
                              >
                                <span className="header-search-category-name">{category.title}</span>
                                <span className="header-search-category-context">{category.context}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {searchResults.length > 0 && (
                      <div className="header-search-section">
                        {categorySearchResults.length > 0 && (
                          <p className="header-search-section-title">Products</p>
                        )}
                        <ul className="header-search-list">
                          {searchResults.map((p) => {
                            const img = p.featuredImage;
                            return (
                              <li key={p.id}>
                                <Link
                                  href={`/products/${p.handle}`}
                                  className="header-search-item"
                                  onClick={() => setSearchOpen(false)}
                                >
                                  <span className="header-search-thumb">
                                    {img?.url ? (
                                      <Image
                                        src={img.url}
                                        alt={img.altText || p.title}
                                        width={80}
                                        height={80}
                                        sizes="40px"
                                      />
                                    ) : null}
                                  </span>
                                  <span className="header-search-meta">
                                    <span className="header-search-name">{p.title}</span>
                                    <span className="header-search-price">
                                      {formatPrice(p.priceRange.minVariantPrice)}
                                    </span>
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {searchLoading && searchResults.length === 0 && (
                      <div className="header-search-status header-search-status-compact">Searching products…</div>
                    )}

                    {!searchLoading && (
                      <Link
                        href={`/search?q=${encodeURIComponent(searchQuery.trim())}`}
                        className="header-search-all"
                        onClick={() => setSearchOpen(false)}
                      >
                        See all results for “{searchQuery.trim()}”
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Header Icons */}
          <div className="header-icons">
            <Link href="/track-order" className="header-icon-link header-icon-desktop-only">
              <TruckIcon />
              <span className="header-icon-label">Track Order</span>
            </Link>
            <div className="header-account-wrapper">
              <Link href="/account" className={`header-icon-link${customer ? ' header-icon-link--loggedin' : ''}`}>
                <UserIcon />
                <span className="header-icon-label">{accountLabel}</span>
              </Link>
            </div>
            <Link href="/cart" className="header-icon-link">
              <span className="header-basket-icon-wrapper">
                <BasketIcon />
                {itemCount > 0 && <span className="header-basket-badge">{itemCount}</span>}
              </span>
              <span className="header-icon-label">Basket</span>
            </Link>
            <Link href="/contact" className="header-icon-link">
              <ContactIcon />
              <span className="header-icon-label">Contact</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Category Tabs + Sub-nav — dynamic from Shopify */}
      {navGroups.length > 0 && (
        <div className="header-nav-wrapper">
          <div className="header-tabs">
            <div className="container header-tabs-inner">
              {navGroups.map((group) => (
                <button
                  key={group.id}
                  ref={(node) => {
                    if (node) {
                      tabRefs.current.set(group.id, node);
                    } else {
                      tabRefs.current.delete(group.id);
                    }
                  }}
                  className={`header-tab ${activeTab === group.id ? 'active' : ''} ${group.highlight ? 'header-tab-highlight' : ''}`}
                  onClick={() => {
                    const isAlreadyOpen =
                      selectedTab === group.id && openMegaId !== null;
                    setSelectedTab(group.id);
                    if (isAlreadyOpen) {
                      setOpenMegaId(null);
                    } else {
                      const firstWithMega = group.items.find(
                        (i) =>
                          (i.products?.length ?? 0) > 0 ||
                          (i.sections?.length ?? 0) > 0
                      );
                      setOpenMegaId(firstWithMega?.href ?? null);
                    }
                  }}
                  type="button"
                >
                  {group.name}
                </button>
              ))}
            </div>
          </div>
          {showSubnav && (
            <nav
              className={`header-subnav ${activeGroup?.highlight ? 'header-subnav-offers' : ''}`}
            >
              <div className="container header-subnav-track" ref={subnavTrackRef}>
                <div
                  className="header-subnav-inner"
                  ref={subnavInnerRef}
                  style={{
                    transform: `translateX(${subnavOffset}px)`,
                  }}
                >
                  {(activeGroup?.items || []).map((item) => {
                    const hasProducts = (item.products?.length ?? 0) > 0;
                    const hasSections = (item.sections?.length ?? 0) > 0;
                    const hasMega = hasProducts || hasSections;
                    const isOpen = openMegaId === item.href;
                    const isCurrent =
                      contextualMenuItem?.href === item.href ||
                      item.href === pathname ||
                      item.sections?.some((section) =>
                        section.items.some((link) => link.href === pathname)
                      );
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`header-subnav-link ${isOpen || (!openMegaId && isCurrent) ? 'active' : ''}`}
                        onClick={(e) => {
                          if (hasMega && !isOpen) {
                            e.preventDefault();
                            openMega(item.href);
                          } else {
                            setOpenMegaId(null);
                          }
                        }}
                        aria-expanded={isOpen}
                        aria-haspopup={hasMega ? 'true' : undefined}
                      >
                        {item.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </nav>
          )}
          {showSubnav && (
            <div className="header-mega-anchor">
              {(activeGroup?.items ?? []).map((item) => {
                const isOpen = openMegaId === item.href;
                const products = item.products ?? [];
                const sections = item.sections ?? [];
                if (products.length === 0 && sections.length === 0) return null;
                return (
                  <div
                    key={item.href}
                    className={`header-mega ${isOpen ? 'open' : ''}`}
                    role="region"
                    aria-label={`${item.title} products`}
                    aria-hidden={!isOpen}
                  >
                    <div className="container header-mega-inner">
                      <div className="header-mega-header">
                        <h3 className="header-mega-title">{item.title}</h3>
                        <Link
                          href={item.href}
                          className="header-mega-shop-all"
                          onClick={() => setOpenMegaId(null)}
                        >
                          Shop all
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M5 12h14" />
                            <path d="m13 6 6 6-6 6" />
                          </svg>
                        </Link>
                      </div>
                      <div className="header-mega-body">
                        {sections.length > 0 ? (
                          <div className="header-mega-category-grid">
                            {sections.map((section, sectionIndex) => (
                              <section
                                key={`${section.title}-${section.items[0]?.href ?? sectionIndex}`}
                                className="header-mega-category-section"
                              >
                                <h4 className="header-mega-category-title">{section.title}</h4>
                                <ul className="header-mega-category-list">
                                  {section.items.map((link) => (
                                    <li key={link.href}>
                                      <Link
                                        href={link.href}
                                        className="header-mega-category-link"
                                        onClick={() => setOpenMegaId(null)}
                                      >
                                        {link.title}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </section>
                            ))}
                          </div>
                        ) : (
                          <ul className="header-mega-list">
                            {products.map((p) => (
                              <li key={p.handle} className="header-mega-list-item">
                                <Link
                                  href={`/products/${p.handle}`}
                                  className="header-mega-list-link"
                                  onClick={() => setOpenMegaId(null)}
                                >
                                  {p.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                        {(() => {
                          const featured = products.find((p) => p.image?.url);
                          if (!featured) return null;
                          return (
                            <Link
                              href={`/products/${featured.handle}`}
                              className="header-mega-feature"
                              onClick={() => setOpenMegaId(null)}
                            >
                              <Image
                                src={featured.image!.url}
                                alt={featured.image!.altText || featured.title}
                                width={320}
                                height={320}
                                sizes="220px"
                              />
                            </Link>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* USP Bar — like Plumbworld */}
      <div className="header-usp">
        <div className="container header-usp-inner">
          <div className="header-usp-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
            <div>
              <span className="header-usp-text">Free Standard Delivery</span>
              <span className="header-usp-sub">Next day &amp; select-a-day options</span>
            </div>
          </div>
          <div className="header-usp-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 14 4 9l5-5"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
            <div>
              <span className="header-usp-text">365-Day Returns</span>
              <span className="header-usp-sub">Returns made easy</span>
            </div>
          </div>
          <div className="header-usp-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            <div>
              <span className="header-usp-text">Lowest Price Guaranteed</span>
              <span className="header-usp-sub">Big brands. Small prices</span>
            </div>
          </div>
          <div className="header-usp-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <div>
              <span className="header-usp-text">Secure Shopping</span>
              <span className="header-usp-sub">Trusted &amp; encrypted payments</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-overlay${mobileMenuOpen ? ' open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden={!mobileMenuOpen}
      >
        <nav className="mobile-menu" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">Menu</span>
            <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </button>
          </div>
          <form
            role="search"
            action="/search"
            className="mobile-menu-search"
            onSubmit={() => setMobileMenuOpen(false)}
          >
            <input
              type="search"
              name="q"
              placeholder="What are you looking for?"
              className="header-search-input"
              autoComplete="off"
            />
          </form>
          <div className="mobile-menu-links">
            {navGroups.map((group) => {
              const isGroupOpen = mobileOpenGroups.includes(group.id);
              return (
                <div key={group.id} className="mobile-menu-group">
                  <button
                    type="button"
                    className={`mobile-menu-link mobile-menu-toggle ${group.highlight ? 'mobile-menu-link-highlight' : ''}`}
                    onClick={() => toggleMobileGroup(group.id)}
                    aria-expanded={isGroupOpen}
                  >
                    <span>{group.name}</span>
                    <span className="mobile-menu-chevron">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {isGroupOpen && (
                    <div className="mobile-menu-panel">
                      <Link
                        href={group.href}
                        className="mobile-menu-shop-all"
                        onClick={closeMobileMenu}
                      >
                        Shop all {group.name}
                      </Link>

                      {group.items.map((item) => {
                        const hasSections = (item.sections?.length ?? 0) > 0;
                        const isItemOpen = mobileOpenItems.includes(item.href);

                        if (!hasSections) {
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              className="mobile-menu-sublink"
                              onClick={closeMobileMenu}
                            >
                              {item.title}
                            </Link>
                          );
                        }

                        return (
                          <div key={item.href} className="mobile-menu-subgroup">
                            <button
                              type="button"
                              className="mobile-menu-sublink mobile-menu-subtoggle"
                              onClick={() => toggleMobileItem(item.href)}
                              aria-expanded={isItemOpen}
                            >
                              <span>{item.title}</span>
                              <span className="mobile-menu-chevron">
                                <ChevronDownIcon />
                              </span>
                            </button>

                            {isItemOpen && (
                              <div className="mobile-menu-sections">
                                <Link
                                  href={item.href}
                                  className="mobile-menu-section-link mobile-menu-section-link-strong"
                                  onClick={closeMobileMenu}
                                >
                                  Shop all {item.title}
                                </Link>
                                {item.sections?.map((section, sectionIndex) => (
                                  <div
                                    key={`${section.title}-${section.items[0]?.href ?? sectionIndex}`}
                                    className="mobile-menu-section"
                                  >
                                    <p className="mobile-menu-section-title">{section.title}</p>
                                    {section.items.map((link) => (
                                      <Link
                                        key={link.href}
                                        href={link.href}
                                        className="mobile-menu-section-link"
                                        onClick={closeMobileMenu}
                                      >
                                        {link.title}
                                      </Link>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mobile-menu-divider" />
          <div className="mobile-menu-links">
            <Link href="/account" className="mobile-menu-link" onClick={closeMobileMenu}>
              <UserIcon /> My Account
            </Link>
            <Link href="/track-order" className="mobile-menu-link" onClick={closeMobileMenu}>
              <TruckIcon /> Track Order
            </Link>
          </div>
        </nav>
      </div>

      <style>{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border: 0;
        }
        .header {
          position: sticky;
          top: 0;
          z-index: 100;
        }

        /* ===== MAIN HEADER ===== */
        .header-main {
          background: var(--color-primary);
          padding: 0.6rem 0;
        }
        .header-main-inner {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        /* Logo */
        .header-logo {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--color-white);
          letter-spacing: -0.025em;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .header-logo-accent { color: var(--color-accent); }

        /* Search */
        .header-search {
          flex: 1;
          max-width: 620px;
          position: relative;
          display: none;
          margin: 0 auto;
        }
        .header-search-input {
          width: 100%;
          padding: 0.65rem 1rem;
          padding-right: 3rem;
          border: 2px solid transparent;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          background: var(--color-white);
          color: var(--color-text-primary);
          outline: none;
          transition: border-color 0.15s;
        }
        .header-search-form {
          position: relative;
          display: block;
          width: 100%;
        }
        .header-search-input:focus {
          border-color: var(--color-accent);
        }
        .header-search-input::placeholder { color: var(--color-text-muted); }
        .header-search-btn {
          position: absolute;
          right: 0.4rem;
          top: 50%;
          transform: translateY(-50%);
          background: var(--color-accent);
          border: none;
          border-radius: 0.375rem;
          padding: 0.4rem;
          cursor: pointer;
          color: var(--color-primary-dark);
          display: flex;
          align-items: center;
          transition: background 0.15s;
        }
        .header-search-btn:hover { background: var(--color-accent-dark); }

        .header-search-dropdown {
          position: absolute;
          top: calc(100% + 0.4rem);
          left: 0;
          right: 0;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
          z-index: 200;
          overflow: hidden;
          max-height: 70vh;
          overflow-y: auto;
        }
        .header-search-status {
          padding: 0.85rem 1rem;
          font-size: 0.85rem;
          color: var(--color-text-secondary);
          text-align: center;
        }
        .header-search-status-compact {
          padding: 0.5rem 1rem 0.65rem;
          font-size: 0.78rem;
        }
        .header-search-section {
          padding: 0.25rem 0;
        }
        .header-search-section + .header-search-section {
          border-top: 1px solid var(--color-border);
        }
        .header-search-section-title {
          margin: 0;
          padding: 0.45rem 0.75rem 0.25rem;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-text-muted);
        }
        .header-search-category-list {
          list-style: none;
          margin: 0;
          padding: 0 0 0.25rem;
        }
        .header-search-category {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.45rem 0.75rem;
          text-decoration: none;
          color: var(--color-text-primary);
          transition: background 0.12s;
        }
        .header-search-category:hover {
          background: var(--color-off-white);
        }
        .header-search-category-name {
          font-size: 0.84rem;
          font-weight: 700;
          line-height: 1.3;
        }
        .header-search-category-context {
          flex: 0 0 auto;
          font-size: 0.72rem;
          color: var(--color-text-muted);
        }
        .header-search-list {
          list-style: none;
          margin: 0;
          padding: 0.25rem 0;
        }
        .header-search-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          color: var(--color-text-primary);
          transition: background 0.12s;
        }
        .header-search-item:hover {
          background: var(--color-off-white);
        }
        .header-search-thumb {
          flex: 0 0 auto;
          width: 40px;
          height: 40px;
          border-radius: 0.35rem;
          background: var(--color-off-white);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .header-search-thumb img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 2px;
          box-sizing: border-box;
        }
        .header-search-meta {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
          min-width: 0;
        }
        .header-search-name {
          font-size: 0.82rem;
          font-weight: 600;
          line-height: 1.3;
          color: var(--color-text-primary);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .header-search-price {
          font-size: 0.8rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .header-search-all {
          display: block;
          padding: 0.7rem 1rem;
          background: var(--color-off-white);
          color: var(--color-primary);
          font-size: 0.82rem;
          font-weight: 600;
          text-decoration: none;
          text-align: center;
          border-top: 1px solid var(--color-border);
          transition: background 0.12s;
        }
        .header-search-all:hover {
          background: var(--color-border);
          color: var(--color-primary-dark);
        }

        /* Phone */
        .header-phone {
          display: none;
          flex-direction: column;
          align-items: flex-end;
          flex-shrink: 0;
          line-height: 1.3;
          margin-left: auto;
        }
        .header-phone-tagline {
          font-size: 0.7rem;
          color: rgba(255,255,255,0.65);
        }
        .header-phone-number {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-white);
          letter-spacing: 0.02em;
        }
        .header-phone-hours {
          font-size: 0.65rem;
          color: rgba(255,255,255,0.55);
        }

        /* Icons */
        .header-icons {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex-shrink: 0;
        }
        .header-icon-link {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.1rem;
          color: var(--color-white);
          padding: 0.3rem 0.5rem;
          transition: color 0.15s;
          position: relative;
        }
        .header-icon-link:hover { color: var(--color-accent); }
        .header-icon-link--loggedin {
          color: #ffe082;
          font-weight: 600;
        }
        .header-icon-link--loggedin:hover { color: #ffd54f; }
        .header-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }
        .header-icon-label {
          font-size: 0.6rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          display: none;
        }
        .header-icon-desktop-only { display: none; }
        .header-basket-icon-wrapper { position: relative; }
        .header-basket-badge {
          position: absolute;
          top: -6px;
          right: -8px;
          background: var(--color-accent);
          color: var(--color-primary-dark);
          font-size: 0.6rem;
          font-weight: 700;
          min-width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Mobile Toggle */
        .header-mobile-toggle {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          background: none;
          border: none;
          color: var(--color-white);
          cursor: pointer;
          padding: 0.25rem;
        }
        .header-mobile-toggle-label {
          font-size: 0.75rem;
          font-weight: 500;
          display: none;
        }

        /* ===== CATEGORY TABS + SUBNAV ===== */
        .header-nav-wrapper { display: none; }
        .header-tabs {
          background: var(--color-white);
          padding: 0.5rem 0 0;
        }
        .header-tabs-inner {
          display: flex;
          justify-content: center;
          gap: 0.35rem;
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .header-tab {
          position: relative;
          padding: 0.6rem 1.75rem;
          font-size: 0.95rem;
          flex: 1;
          font-weight: 600;
          color: var(--color-white);
          background: var(--color-primary);
          border: none;
          border-radius: 1rem 1rem 0 0;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .header-tab::after {
          content: '';
          position: absolute;
          left: 1.2rem;
          right: 1.2rem;
          bottom: 0;
          height: 0;
          background: transparent;
        }
        .header-tab:hover {
          background: var(--color-primary-dark);
        }
        .header-tab:hover::after,
        .header-tab:focus-visible::after,
        .header-tab.active::after {
          transform: scaleX(1);
        }
        .header-tab:focus-visible {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
        }
        .header-tab.active {
          background: var(--color-primary-dark);
          color: var(--color-white);
        }
        .header-tab-highlight {
          background: #cf3f43 !important;
          color: var(--color-white) !important;
        }
        .header-tab-highlight:hover {
          background: #b8363a !important;
        }
        .header-tab-highlight.active {
          background: #cf3f43 !important;
          color: var(--color-white) !important;
        }
        .header-nav-wrapper {
          position: relative;
        }
        .header-subnav {
          display: block;
          background: var(--color-primary-dark);
          min-height: 2.5rem;
          position: relative;
          z-index: 5;
        }
        .header-subnav-offers {
          background: #cf3f43;
        }
        .header-subnav-track {
          max-width: 1100px;
          min-height: 2.5rem;
          margin: 0 auto;
          padding: 0 1rem;
          overflow: visible;
        }
        .header-subnav-inner {
          display: inline-flex;
          justify-content: flex-start;
          flex-wrap: nowrap;
          gap: 0.25rem;
          width: max-content;
          transition: transform 0.16s ease;
        }
        .header-subnav-link {
          display: inline-flex;
          align-items: center;
          padding: 0.65rem 1.25rem;
          font-size: 0.82rem;
          font-weight: 500;
          color: rgba(255,255,255,0.9);
          white-space: nowrap;
          text-decoration: none;
          transition: color 0.15s, background 0.15s;
        }
        .header-subnav-link:hover,
        .header-subnav-link.active {
          color: var(--color-white);
          background: rgba(255,255,255,0.12);
        }

        /* ===== MEGA MENU DROPDOWN ===== */
        .header-mega-anchor {
          position: relative;
          width: 100%;
          height: 0;
        }
        .header-mega {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          background: var(--color-white);
          border-bottom: 1px solid var(--color-border);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          z-index: 150;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transform: translateY(-6px);
          transition:
            opacity 0.12s ease,
            transform 0.12s ease-in,
            visibility 0s linear 0.12s;
        }
        .header-mega.open {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateY(0);
          transition:
            opacity 0.2s ease,
            transform 0.2s cubic-bezier(0.22, 1, 0.36, 1),
            visibility 0s linear 0s;
        }
        .header-mega-inner {
          max-width: 1100px;
          padding: 1rem 1rem 1.25rem;
        }
        .header-mega-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 0.6rem;
          margin-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-border);
        }
        .header-mega-title {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--color-text-primary);
          margin: 0;
        }
        .header-mega-shop-all {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-primary);
          text-decoration: none;
          transition: color 0.15s, gap 0.2s ease;
        }
        .header-mega-shop-all:hover {
          color: var(--color-primary-dark);
          gap: 0.5rem;
        }
        .header-mega-body {
          display: flex;
          gap: 2rem;
          align-items: flex-start;
        }
        .header-mega-list {
          list-style: none;
          padding: 0;
          margin: 0;
          flex: 1 1 auto;
          min-width: 0;
          column-count: 3;
          column-gap: 2rem;
        }
        @media (max-width: 900px) {
          .header-mega-list { column-count: 2; }
        }
        .header-mega-feature {
          flex: 0 0 170px;
          display: block;
          aspect-ratio: 1;
          border: 1px solid var(--color-border);
          border-radius: 0.6rem;
          overflow: hidden;
          background: var(--color-white);
          transition: border-color 0.15s, transform 0.15s;
        }
        .header-mega-feature:hover {
          border-color: var(--color-primary);
          transform: translateY(-2px);
        }
        .header-mega-feature img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 0.75rem;
          box-sizing: border-box;
        }
        .header-mega-feature-strip {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: repeat(2, 150px);
          gap: 0.75rem;
        }
        .header-mega-feature-card {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 0.55rem;
          border: 1px solid var(--color-border);
          border-radius: 0.6rem;
          background: var(--color-white);
          text-decoration: none;
          color: var(--color-text-primary);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .header-mega-feature-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(0,0,0,0.08);
        }
        .header-mega-feature-card img {
          width: 100%;
          height: 110px;
          object-fit: contain;
        }
        .header-mega-feature-title {
          font-size: 0.72rem;
          font-weight: 500;
          line-height: 1.25;
          color: var(--color-text-secondary);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        @media (max-width: 1100px) {
          .header-mega-feature-strip { grid-template-columns: repeat(2, 130px); }
        }
        @media (max-width: 900px) {
          .header-mega-feature-strip { display: none; }
        }
        @media (max-width: 768px) {
          .header-mega-feature { display: none; }
        }
        .header-mega-list-item {
          break-inside: avoid;
          margin: 0 0 0.15rem;
        }
        .header-mega-list-link {
          display: block;
          padding: 0.22rem 0.4rem;
          font-size: 0.78rem;
          color: var(--color-text-secondary);
          text-decoration: none;
          line-height: 1.35;
          border-left: 2px solid transparent;
          transition: color 0.15s, border-color 0.15s, padding-left 0.15s;
          border-radius: 0 0.25rem 0.25rem 0;
        }
        .header-mega-list-link:hover {
          color: var(--color-primary);
          border-left-color: var(--color-primary);
          padding-left: 0.65rem;
          background: rgba(27,63,35,0.04);
        }
        .header-mega-category-grid {
          flex: 1 1 auto;
          display: grid;
          grid-template-columns: repeat(4, minmax(150px, 1fr));
          gap: 1.15rem 1.75rem;
        }
        .header-mega-category-section {
          min-width: 0;
        }
        .header-mega-category-title {
          margin: 0 0 0.4rem;
          padding-bottom: 0.35rem;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.78rem;
          font-weight: 700;
          color: var(--color-primary);
          line-height: 1.25;
        }
        .header-mega-category-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .header-mega-category-link {
          display: block;
          padding: 0.2rem 0;
          font-size: 0.82rem;
          font-weight: 500;
          color: var(--color-text-secondary);
          text-decoration: none;
          line-height: 1.35;
          transition: color 0.15s;
        }
        .header-mega-category-link:hover {
          color: var(--color-primary);
          text-decoration: underline;
          text-underline-offset: 0.18em;
        }
        .header-mega-category-link.active {
          color: var(--color-primary);
          font-weight: 700;
        }
        @media (max-width: 1000px) {
          .header-mega-category-grid {
            grid-template-columns: repeat(3, minmax(150px, 1fr));
          }
        }
        @media (max-width: 768px) {
          .header-mega-category-grid {
            grid-template-columns: repeat(2, minmax(140px, 1fr));
          }
        }

        /* ===== USP BAR ===== */
        .header-usp {
          background: var(--color-white);
          border-bottom: 1px solid var(--color-border);
          padding: 0;
          display: none;
        }
        .header-usp-inner {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
        }
        .header-usp-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--color-primary);
        }
        .header-usp-item svg { flex-shrink: 0; }
        .header-usp-text {
          font-size: 0.8rem;

          font-weight: 600;
          color: var(--color-text-primary);
          display: block;
        }
        .header-usp-sub {
          font-size: 0.7rem;
          color: var(--color-text-muted);
          display: block;
        }

        /* ===== MOBILE MENU ===== */
        .mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 200;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition:
            opacity 0.14s ease-in,
            visibility 0s linear 0.14s;
        }
        .mobile-overlay.open {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transition:
            opacity 0.25s ease,
            visibility 0s linear 0s;
        }
        .mobile-menu {
          position: absolute;
          top: 0;
          left: 0;
          width: 85%;
          max-width: 340px;
          height: 100%;
          background: var(--color-white);
          overflow-y: auto;
          box-shadow: 4px 0 20px rgba(0,0,0,0.15);
          transform: translateX(-100%);
          transition: transform 0.14s ease-in;
        }
        .mobile-overlay.open .mobile-menu {
          transform: translateX(0);
          transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: var(--color-primary);
          color: var(--color-white);
        }
        .mobile-menu-title {
          font-weight: 700;
          font-size: 1rem;
        }
        .mobile-menu-close {
          background: none;
          border: none;
          color: var(--color-white);
          cursor: pointer;
          padding: 0.25rem;
        }
        .mobile-menu-search {
          padding: 0.75rem 1rem;
          background: var(--color-off-white);
          border-bottom: 1px solid var(--color-border);
        }
        .mobile-menu-search .header-search-input {
          border: 1px solid var(--color-border);
          padding-right: 1rem;
        }
        .mobile-menu-links { padding: 0.25rem 0; }
        .mobile-menu-group {
          border-bottom: 1px solid var(--color-border);
        }
        .mobile-menu-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: 100%;
          padding: 0.8rem 1.25rem;
          background: transparent;
          border: 0;
          color: var(--color-text-primary);
          font-size: 0.9rem;
          font-weight: 500;
          font-family: inherit;
          text-align: left;
          transition: background 0.15s;
          cursor: pointer;
        }
        .mobile-menu-link:hover {
          background: var(--color-off-white);
          color: var(--color-primary);
        }
        .mobile-menu-link-highlight {
          color: #cf3f43;
          font-weight: 700;
        }
        .mobile-menu-toggle[aria-expanded="true"],
        .mobile-menu-subtoggle[aria-expanded="true"] {
          color: var(--color-primary);
          background: var(--color-off-white);
        }
        .mobile-menu-chevron {
          display: inline-flex;
          flex: 0 0 auto;
          transition: transform 0.15s ease;
        }
        .mobile-menu-toggle[aria-expanded="true"] .mobile-menu-chevron,
        .mobile-menu-subtoggle[aria-expanded="true"] .mobile-menu-chevron {
          transform: rotate(180deg);
        }
        .mobile-menu-panel {
          padding: 0.25rem 0 0.65rem;
          background: #fbfbf8;
        }
        .mobile-menu-shop-all,
        .mobile-menu-sublink,
        .mobile-menu-section-link {
          display: flex;
          align-items: center;
          width: 100%;
          color: var(--color-text-primary);
          text-decoration: none;
          font-size: 0.84rem;
          line-height: 1.35;
          font-family: inherit;
        }
        .mobile-menu-shop-all {
          padding: 0.55rem 1.25rem 0.55rem 1.75rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .mobile-menu-sublink {
          justify-content: space-between;
          padding: 0.58rem 1.25rem 0.58rem 1.75rem;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: left;
        }
        .mobile-menu-sublink:hover,
        .mobile-menu-section-link:hover {
          color: var(--color-primary);
          background: rgba(27,63,35,0.04);
        }
        .mobile-menu-subgroup {
          border-top: 1px solid rgba(0,0,0,0.05);
        }
        .mobile-menu-sections {
          padding: 0.2rem 0 0.65rem;
          background: var(--color-white);
        }
        .mobile-menu-section {
          padding: 0.4rem 0 0;
        }
        .mobile-menu-section-title {
          margin: 0;
          padding: 0.3rem 1.25rem 0.25rem 2.25rem;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-text-muted);
        }
        .mobile-menu-section-link {
          padding: 0.42rem 1.25rem 0.42rem 2.25rem;
          color: var(--color-text-secondary);
        }
        .mobile-menu-section-link-strong {
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .mobile-menu-divider + .mobile-menu-links .mobile-menu-link {
          justify-content: flex-start;
          border-bottom: 1px solid var(--color-border);
        }
        .mobile-menu-divider {
          height: 6px;
          background: var(--color-off-white);
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
        }
        .mobile-menu-footer {
          padding: 1rem 1.25rem;
        }
        .mobile-menu-phone {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--color-primary);
          font-weight: 600;
          font-size: 0.75rem;
        }

        /* ===== RESPONSIVE ===== */
        @media (min-width: 640px) {
          .header-search { display: block; }
          .header-mobile-toggle-label { display: inline; }
          .header-icon-label { display: block; }
        }

        @media (min-width: 1024px) {
          .header-mobile-toggle { display: none; }
          .header-nav-wrapper { display: block; }
          .header-usp { display: block; }
          .header-icon-desktop-only { display: flex; }
          .header-logo { font-size: 1.5rem; }
        }

        @media (min-width: 1200px) {
          .header-phone { display: flex; }
        }
      `}</style>
    </header>
  );
}
